import type { Control } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { restoreWhenLeftEmpty } from "~/utils/form-utils";
import { googleDriveSyncDefaultValues, PARSER_OPTIONS, type GoogleDriveSyncForm } from "~/utils/google-drive-sync-form";
import { Card, Collapse, Form, FormItem, NumberField, Select, Switch } from "~/components/ui";

type SchemaOption = { value: string; label: string };

type FolderOption = { value: string; label: string };

type GoogleDriveSyncConfigFormProps = {
  control: Control<GoogleDriveSyncForm>;
  folderOptions: FolderOption[];
  foldersLoading: boolean;
  schemaOptions: SchemaOption[];
  schemasLoading: boolean;
};

export function GoogleDriveSyncConfigForm({
  control,
  folderOptions,
  foldersLoading,
  schemaOptions,
  schemasLoading,
}: GoogleDriveSyncConfigFormProps) {
  const { t } = useTranslation("admin");
  const limitEnabled = useWatch({ control, name: "limitEnabled" });

  const advancedItems = [
    {
      key: "advanced",
      label: t("uploadAdmin.advanced.title"),
      children: (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Controller
              name="skipExisting"
              control={control}
              render={({ field }) => (
                <Switch
                  size="small"
                  checked={field.value}
                  onChange={field.onChange}
                  aria-label={t("uploadAdmin.advanced.skipExisting")}
                />
              )}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.skipExisting")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Controller
              name="limitEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  size="small"
                  checked={field.value}
                  onChange={field.onChange}
                  aria-label={t("uploadAdmin.advanced.maxRecords")}
                />
              )}
            />
            <span className="text-noa">{t("uploadAdmin.advanced.maxRecords")}</span>
            {limitEnabled && (
              <Controller
                name="maxRecords"
                control={control}
                render={({ field }) => (
                  <NumberField
                    value={field.value}
                    // Emptying the field reports null, which the schema rejects even with the limit
                    // off: ignore it while typing and fall back to the default if left empty.
                    onChange={(value) => value !== null && field.onChange(value)}
                    onBlur={restoreWhenLeftEmpty(googleDriveSyncDefaultValues.maxRecords, field.onChange, field.onBlur)}
                    min={1}
                    max={10000}
                    format={{ maximumFractionDigits: 0 }}
                    size="small"
                    className="w-20"
                  />
                )}
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card title={t("uploadAdmin.sync.googleDrive.configuration")} size="small">
        <Form>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormItem
              label={t("uploadAdmin.sync.googleDrive.folder")}
              tooltip={t("uploadAdmin.sync.googleDrive.folderHint")}
              className="mb-0"
            >
              <Controller
                name="folder"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("uploadAdmin.sync.googleDrive.allFolders")}
                    allowClear
                    loading={foldersLoading}
                    options={folderOptions}
                  />
                )}
              />
            </FormItem>

            <FormItem label={t("uploadAdmin.form.schema")} tooltip={t("uploadAdmin.form.schemaHint")} className="mb-0">
              <Controller
                name="schemaId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("uploadAdmin.form.schemaPlaceholder")}
                    allowClear
                    loading={schemasLoading}
                    options={schemaOptions}
                  />
                )}
              />
            </FormItem>

            <FormItem
              label={t("uploadAdmin.form.parser")}
              tooltip={t("uploadAdmin.sync.googleDrive.parserHint")}
              className="mb-0"
            >
              <Controller
                name="parser"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder={t("uploadAdmin.form.parserPlaceholder")}
                    allowClear
                    options={[...PARSER_OPTIONS]}
                  />
                )}
              />
            </FormItem>
          </div>
        </Form>
      </Card>

      <Collapse items={advancedItems} size="small" />
    </>
  );
}
